import { Injectable } from '@angular/core';
import { AccessService } from '@zaoblako/web/core/models/enums/access-rules/access.service';
import { ProjectChecklistService } from '@zaoblako/web/core/services/project/project-checklist.service';
import { ProjectService } from '@zaoblako/web/core/services/project/project.service';
import { StoreService } from '@zaoblako/web/core/services/store.service';
import { KeysOfType } from '@zaoblako/web/core/types/keys-of.type';
import { ProjectCardAccessRights, ProjectState } from '@zaoblako/web/modules/project/components/project/card/models/project-state';
import { projectInitialState } from '@zaoblako/web/modules/project/components/project/card/state/project-initial-state';
import { CardEntityPatchBaseline } from '@zaoblako/web/shared/components/card-layout/universal-card-v2/card-entity-patch-baseline.util';
import { collectCardPatchableFields, shouldMergeCardPatchResponseFromRelation } from '@zaoblako/web/shared/utils/card-descriptor-patch-field.util';
import type { CardDescriptorInterface } from '@zaoblako/shared/common/dtos/card/card-descriptor.dto';
import type { CardFieldDescriptorInterface } from '@zaoblako/shared/common/dtos/card/card-field-descriptor.dto';
import type { DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import type { ClientFindInterface } from '@zaoblako/shared/modules/client/dtos/client/client-find.dto';
import type { ChecklistFindInterface } from '@zaoblako/shared/modules/project/dto/checklist/checklist-find.dto';
import type { ProjectPageInterface } from '@zaoblako/shared/modules/project/dto/project/project-page.dto';
import type { ProjectDealQueryInterface } from '@zaoblako/shared/modules/project/dto/project/project-deal-query.dto';
import type { ProjectHelpdeskTasksQueryInterface } from '@zaoblako/shared/modules/project/dto/project/project-helpdesk-tasks-query.dto';
import type { ProjectListQueryInterface } from '@zaoblako/shared/modules/project/dto/project/project-list-query.dto';
import type { ProjectPartialUpdateInterface } from '@zaoblako/shared/modules/project/dto/project/project-partial-update.dto';
import type { ProjectFindInterface } from '@zaoblako/shared/modules/project/dto/project/project-find.dto';
import type { SubprojectDefaultsFindInterface } from '@zaoblako/shared/modules/project/dto/subproject-defaults/subproject-defaults-find.dto';
import type { ChecklistQueryInterface } from '@zaoblako/shared/modules/project/dto/checklist/checklist-query.dto';

import { BehaviorSubject, forkJoin, Observable, of, Subject, Subscription, tap } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class ProjectStoreService extends StoreService<ProjectState> {
  protected state = new BehaviorSubject(projectInitialState);
  public readonly breadcrumbsLoaded$ = new Subject<ProjectFindInterface[]>();

  private cardPatchFields = new Map<string, CardFieldDescriptorInterface>();
  private cardPatchFieldsLoadSub?: Subscription;
  private readonly patchBaseline = new CardEntityPatchBaseline<ProjectFindInterface>();

  public get getState() {
    return this.state.value;
  }

  public get getProject(): ProjectFindInterface {
    return this.getState.project;
  }

  public get accessRights(): ProjectCardAccessRights {
    return {
      canWriteProject: this.accessService.project.projectRights.canWriteProject,
      canWriteTemplate: this.accessService.project.projectRights.canWriteTemplate,
      canWriteEventCalendar: this.accessService.project.projectRights.canWriteEventCalendar,
      canReadHelpdesk: this.accessService.helpdesk.helpdeskRights.canReadHelpdesk,
      canReadDeal: this.accessService.finance.dealRights.canReadDeal
    };
  }

  public constructor(
    private readonly projectService: ProjectService,
    private readonly projectChecklistService: ProjectChecklistService,
    private readonly accessService: AccessService
  ) {
    super();
  }

  public setEntities(entities: {
    directions: DirectionFindInterface[];
    clients: ClientFindInterface[];
  }) {
    this.updateState({
      directions: entities.directions,
      clients: entities.clients
    });
  }

  public updatePanelsInfo(key: KeysOfType<typeof this.getState.panelsInfo, any>, value: any) {
    const info = { ...this.getState.panelsInfo, [key]: value };
    this.updateState({ panelsInfo: info });
    localStorage.setItem('project.panelsInfo', JSON.stringify(info));

    if ((key === 'subprojectsShow' || key === 'calendarChecklistsShow') && value === true) {
      this.refreshCalendarEvents();
    }
  }

  public loadPanelsInfo() {
    if (localStorage.getItem('project.panelsInfo')) {
      const panelsInfo = JSON.parse(localStorage.getItem('project.panelsInfo'));
      Object.keys(this.getState.panelsInfo).forEach((key) => {
        panelsInfo[key] = panelsInfo.hasOwnProperty(key) ? panelsInfo[key] : this.getState.panelsInfo[key];
      });
      this.updateState({ panelsInfo });
    }
  }

  public applyCardDescriptor(descriptor: CardDescriptorInterface | null): void {
    this.cardPatchFields = collectCardPatchableFields(descriptor);
  }

  public ensureCardPatchFieldsLoaded(): void {
    if (this.cardPatchFields.size > 0 || this.cardPatchFieldsLoadSub) {
      return;
    }

    this.cardPatchFieldsLoadSub = this.projectService.getCardDescriptor().subscribe({
      next: (descriptor) => {
        this.applyCardDescriptor(descriptor);
        this.cardPatchFieldsLoadSub = undefined;
      },
      error: () => {
        this.cardPatchFieldsLoadSub = undefined;
      }
    });
  }

  public syncPatchBaselineFromProject = (): void => {
    this.patchBaseline.sync(this.getProject ?? null);
  };

  public getPatchFieldBaseline = (key: string): unknown => this.patchBaseline.fieldValue(key);

  public isPatchFieldUnchanged = (key: string, value: unknown): boolean =>
    this.patchBaseline.isFieldUnchanged(key, value);

  public updateFieldProject(
    key: keyof ProjectPartialUpdateInterface,
    value: unknown
  ): Observable<ProjectFindInterface | null> {
    const project: ProjectFindInterface = {
      ...this.getProject,
      [key]: (value ?? null) as ProjectFindInterface[keyof ProjectFindInterface]
    };
    this.updateState({ project });

    const projectId = this.getProject?.id;
    if (!projectId) {
      return of(null);
    }

    if (this.patchBaseline.isFieldUnchanged(String(key), value)) {
      return of(this.getProject);
    }

    this.ensureCardPatchFieldsLoaded();

    return this.projectService
      .patch<ProjectFindInterface, ProjectFindInterface>({ id: projectId, [key]: value ?? null })
      .pipe(
        tap((updated) => {
          this.patchBaseline.commitField(String(key), value);
          const fieldDescriptor = this.cardPatchFields.get(String(key));
          if (shouldMergeCardPatchResponseFromRelation(fieldDescriptor) && updated?.[key]) {
            this.updateState({ project: { ...this.getProject, [key]: updated[key] } });
          }
        }),
        switchMap((updated) =>
          key === 'parent' ? this.loadBreadcrumbs(projectId).pipe(map(() => updated)) : of(updated)
        )
      );
  }

  public updateFieldsProject(keys: (keyof ProjectPartialUpdateInterface)[]): Observable<ProjectFindInterface | null> {
    const currentProject = this.getProject;
    if (!currentProject?.id || keys.length === 0) {
      return of(null);
    }

    let project: ProjectFindInterface = { ...currentProject };
    let patchPayload: ProjectPartialUpdateInterface & { id: number; } = { id: currentProject.id };
    for (const key of keys) {
      const value = currentProject[key as keyof ProjectFindInterface];
      project = {
        ...project,
        [key]: (value ?? null) as ProjectFindInterface[keyof ProjectFindInterface]
      };
      patchPayload = { ...patchPayload, [key]: value ?? null };
    }
    this.updateState({ project });
    this.ensureCardPatchFieldsLoaded();

    return this.projectService.patch<ProjectFindInterface, ProjectFindInterface>(patchPayload).pipe(
      tap((updated) => {
        for (const key of keys) {
          const fieldDescriptor = this.cardPatchFields.get(String(key));
          if (shouldMergeCardPatchResponseFromRelation(fieldDescriptor) && updated?.[key]) {
            this.updateState({ project: { ...this.getProject, [key]: updated[key] } });
          }
        }
      }),
      switchMap((updated) =>
        keys.includes('parent') ? this.loadBreadcrumbs(currentProject.id).pipe(map(() => updated)) : of(updated)
      )
    );
  }

  public addChecklist(checklist: ChecklistFindInterface) {
    let checklists = this.getState.checklistsInfo.checklists;
    checklists.push(checklist);
    checklists = [...checklists];
    this.updateState((state) => ({
      checklistsInfo: {
        ...state.checklistsInfo,
        checklists,
        loading: this.getState.checklistsInfo?.loading
      }
    }));

    if (checklist['project']?.id) {
      this.invalidateCalendarCache(checklist['project']?.id);
    }
  }

  public setState(state: ProjectState) {
    this.updateState(state);
  }

  public updateChecklist(checklist: ChecklistFindInterface) {
    const checklists = this.getState.checklistsInfo.checklists.filter((_cl) => _cl.id !== checklist.id);
    checklists.push(checklist);
    this.updateState((state) => ({
      checklistsInfo: {
        ...state.checklistsInfo,
        checklists,
        loading: this.getState.checklistsInfo?.loading
      }
    }));

    if (checklist['project']?.id) {
      this.invalidateCalendarCache(checklist['project']?.id);
    }
  }

  public deleteChecklist(checklistId: number) {
    const checklist = this.getState.checklistsInfo.checklists.find((c) => c.id === checklistId);
    if (checklist['project']?.id) {
      this.invalidateCalendarCache(checklist['project']?.id);
    }

    const checklists = this.getState.checklistsInfo.checklists.filter((checklist) => checklist.id !== checklistId);
    this.updateState((state) => ({
      checklistsInfo: {
        ...state.checklistsInfo,
        checklists,
        loading: this.getState.checklistsInfo?.loading
      }
    }));
  }

  public loadSubprojects(queryParams: ProjectListQueryInterface) {
    const projectId = this.getProjectId();
    if (projectId === null) {
      return;
    }

    this.updateState({
      subprojectsInfo: {
        count: this.getState.subprojectsInfo.count,
        projects: this.getState.subprojectsInfo.projects,
        queryParams,
        loading: true,
        paginationLoading: this.getState.subprojectsInfo.paginationLoading
      }
    });

    let projectsMethod: Observable<ProjectPageInterface>;
    if (this.getState.subprojectsInfo?.queryParams?.listStyle === 'hierarchy') {
      projectsMethod = this.projectService.subprojectHierarchy(
        projectId,
        this.getState.subprojectsInfo?.queryParams
      );
    } else if (this.getState.subprojectsInfo?.queryParams?.listStyle === 'flat') {
      projectsMethod = this.projectService.subprojectFlat(
        projectId,
        this.getState.subprojectsInfo?.queryParams
      );
    }

    projectsMethod.subscribe({
      next: (data) => {
        this.updateState({
          subprojectsInfo: {
            count: data?.count,
            projects: data?.items,
            queryParams: this.getState.subprojectsInfo?.queryParams,
            loading: false,
            paginationLoading: this.getState.subprojectsInfo?.paginationLoading
          }
        });
      }
    });
  }

  public onScrollEndSubprojects() {
    let subprojectsPanel = this.getState.subprojectsInfo;
    if (
      subprojectsPanel?.projects?.length < subprojectsPanel?.count &&
      !subprojectsPanel?.loading &&
      !subprojectsPanel?.paginationLoading
    ) {
      this.updateState({
        subprojectsInfo: {
          loading: subprojectsPanel.loading,
          count: subprojectsPanel.count,
          projects: subprojectsPanel.projects,
          queryParams: {
            ...subprojectsPanel.queryParams,
            offset: subprojectsPanel.queryParams.offset + subprojectsPanel.queryParams.limit
          },
          paginationLoading: true
        }
      });

      let projectsMethod: Observable<ProjectPageInterface>;
      if (subprojectsPanel.queryParams?.listStyle === 'hierarchy') {
        projectsMethod = this.projectService.subprojectHierarchy(this.getProject?.id, subprojectsPanel.queryParams);
      } else if (subprojectsPanel.queryParams?.listStyle === 'flat') {
        projectsMethod = this.projectService.subprojectFlat(this.getProject?.id, subprojectsPanel.queryParams);
      }

      projectsMethod.subscribe({
        next: (data) => {
          subprojectsPanel = this.getState.subprojectsInfo;
          this.updateState({
            subprojectsInfo: {
              count: data?.count,
              projects: subprojectsPanel.projects.concat(data.items),
              queryParams: subprojectsPanel.queryParams,
              loading: subprojectsPanel.loading,
              paginationLoading: false
            }
          });
        }
      });
    }
  }

  public loadHelpdeskTasks(queryParams: ProjectHelpdeskTasksQueryInterface) {
    this.updateState({
      helpdeskTasksInfo: {
        tasks: this.getState.helpdeskTasksInfo.tasks,
        queryParams,
        loading: true
      }
    });

    this.projectService
      .getHelpdeskTasks(this.getState.project?.id, this.getState.helpdeskTasksInfo.queryParams)
      .subscribe({
        next: (tasks) => {
          this.updateState({
            helpdeskTasksInfo: {
              tasks,
              queryParams: this.getState.helpdeskTasksInfo.queryParams,
              loading: false
            }
          });
        }
      });
  }

  public loadChecklists(queryParams: ChecklistQueryInterface = { onlyMy: false }) {
    const projectId = this.getProjectId();
    if (projectId === null) {
      return;
    }

    this.updateState({
      checklistsInfo: {
        checklists: this.state.value.checklistsInfo.checklists,
        queryParams,
        loading: true
      }
    });

    this.projectChecklistService
      .getChecklistsByProjectId(projectId, this.getState.checklistsInfo.queryParams)
      .subscribe({
        next: (checklists) => {
          this.updateState({
            checklistsInfo: {
              checklists,
              queryParams: this.getState.checklistsInfo.queryParams,
              loading: false
            }
          });
        },
        error: () => {
          this.updateState((state) => ({
            checklistsInfo: {
              ...state.checklistsInfo,
              checklists: [],
              loading: false
            }
          }));
        }
      });
  }

  public loadFiles() {
    const projectId = this.getProjectId();
    if (projectId === null) {
      return;
    }

    this.updateState({
      filesInfo: {
        files: this.getState.filesInfo.files,
        loading: true
      }
    });

    this.projectService.getFiles(projectId).subscribe({
      next: (files) => {
        this.updateState({
          filesInfo: {
            files,
            loading: false
          }
        });
      }
    });
  }

  public loadDeals(queryParams: ProjectDealQueryInterface) {
    const projectId = this.getProjectId();
    if (projectId === null) {
      return;
    }

    this.updateState({
      dealsInfo: {
        deals: this.getState.dealsInfo.deals,
        queryParams,
        loading: true
      }
    });

    this.projectService.getDeals(projectId, this.getState.dealsInfo.queryParams).subscribe({
      next: (deals) => {
        this.updateState({
          dealsInfo: {
            deals,
            queryParams: this.getState.dealsInfo.queryParams,
            loading: false
          }
        });
      }
    });
  }

  public getFactTime() {
    const projectId = this.getProjectId();
    if (projectId === null) {
      return;
    }

    this.projectService.getFactTime(projectId).subscribe({
      next: (data) => {
        const project: ProjectFindInterface = { ...this.getState.project };
        project.factMin = data.factMin;
        this.updateState({ project });
      }
    });
  }

  public loadDefaults() {
    const projectId = this.getProjectId();
    if (projectId === null) {
      return;
    }

    this.updateState({
      subprojectDefaults: {
        defaults: this.getState.subprojectDefaults.defaults,
        loading: true
      }
    });

    this.projectService.getSubprojectDefaults(projectId).subscribe({
      next: (defaults) => {
        this.updateState({
          subprojectDefaults: {
            defaults,
            loading: false
          }
        });
      }
    });
  }

  public updateDefaults(defaults: SubprojectDefaultsFindInterface) {
    this.updateState({
      subprojectDefaults: {
        defaults,
        loading: this.getState.checklistsInfo?.loading
      }
    });
  }

  public updateFilesCount(count: number) {
    this.updateState((state) => ({
      project: {
        ...state.project,
        filesCount: state.project.filesCount + count
      }
    }));
  }

  public loadBreadcrumbs(projectId: number): Observable<ProjectFindInterface[]> {
    return this.projectService.breadcrumbs(projectId).pipe(
      tap((projects) => {
        this.breadcrumbsLoaded$.next(projects);
        const projectBreadcrumbs = projects.map((project) => {
          return {
            label: project.name,
            routerLink: `/project/card/${project.id}`
          };
        });
        this.updateState({
          projectBreadcrumbs
        });
      })
    );
  }

  public updateExpandedProject(id: number, isExpanded: boolean) {
    const currentIds = this.getState.subprojectsInfo?.expandedProjectIds || [];
    const expandedProjectIds = isExpanded ? [...new Set([...currentIds, id])] : currentIds.filter((pId) => pId !== id);

    this.updateState({
      subprojectsInfo: {
        ...this.getState.subprojectsInfo,
        expandedProjectIds
      }
    });

    if (this.getState.panelsInfo?.calendarChecklistsShow) {
      this.refreshCalendarEvents();
    }
  }

  public refreshCalendarEvents() {
    const mainProjectId = this.getProjectId();
    if (mainProjectId === null) {
      return;
    }
    const expandedIds = this.getState.panelsInfo?.subprojectsShow
      ? this.getState.subprojectsInfo?.expandedProjectIds || []
      : [];

    this.loadCalendarEvents([mainProjectId, ...expandedIds]);
  }

  public loadCalendarEvents(projectIds: number[]) {
    const validProjectIds = projectIds.filter((id) => typeof id === 'number' && !isNaN(id));
    if (!validProjectIds.length) {
      return;
    }

    const mainProjectId = validProjectIds[0];
    const cache = this.getState.calendarInfo.cache;
    const idsToMap = validProjectIds.filter((id) => !cache.has(id));

    if (!idsToMap.length) {
      this.updateCalendarItemsFromCache(validProjectIds);
      return;
    }

    this.updateState({
      calendarInfo: { ...this.getState.calendarInfo, loading: true }
    });

    const requests = idsToMap.map((id) => {
      const isRoot = mainProjectId === id;
      return forkJoin({
        id: of(id),
        ownEvents: isRoot ? this.projectChecklistService.getChecklistsByProjectId(id, { children: false }) : of([]),
        childEvents: this.projectChecklistService.getChecklistsByProjectId(id, { children: true }),
        subprojects: this.projectService.subprojectHierarchy(id).pipe(map((data) => data.items))
      }).pipe(catchError(() => of(null)));
    });

    forkJoin(requests).subscribe((results) => {
      const newCache = new Map(cache);

      results.forEach((res) => {
        if (!res) return;

        const validSubprojects = res.subprojects.filter((p) => !!p.deadline);
        const combined = [...res.ownEvents, ...res.childEvents, ...validSubprojects];

        newCache.set(res.id, {
          items: combined,
          timestamp: Date.now()
        });
      });

      this.updateState({
        calendarInfo: {
          ...this.getState.calendarInfo,
          cache: newCache
        }
      });

      this.updateCalendarItemsFromCache(validProjectIds);
    });
  }

  private getProjectId(): number | null {
    const id = this.getProject?.id;
    if (typeof id !== 'number' || isNaN(id)) {
      return null;
    }
    return id;
  }

  private updateCalendarItemsFromCache(activeIds: number[]) {
    const cache = this.getState.calendarInfo.cache;
    let allItems: (ChecklistFindInterface | ProjectFindInterface)[] = [];

    activeIds.forEach((id) => {
      const cached = cache.get(id);
      if (cached) {
        allItems = [...allItems, ...cached.items];
      }
    });

    const uniqueItems = allItems.filter((item, index, self) => {
      const isProject = 'deadline' in item;
      return index === self.findIndex((p) => p.id === item.id && 'deadline' in p === isProject);
    });

    this.updateState({
      calendarInfo: {
        ...this.getState.calendarInfo,
        currentRootId: activeIds[0],
        items: uniqueItems,
        loading: false
      }
    });
  }

  public invalidateCalendarCache(projectId?: number) {
    if (projectId) {
      this.getState.calendarInfo.cache.delete(projectId);
    } else {
      this.getState.calendarInfo.cache.clear();
    }
    this.refreshCalendarEvents();
  }
}
